import React, {useEffect, useState, useRef} from 'react';
import {useToast} from '../../contexts/toastContext/ToastContext';
import TileButtonGroup from './AurTileButtonGroup';
import AurDetailsContent from './AurDetailsContent';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../redux/store';
import {Typography, Box, Collapse} from '@mui/material';
import {Department} from '../../types/types';
import {selectUser} from '../../redux/slices/userSlice';
import {
  setClientDetailsFormState,
  setDrawerAccordionDisable,
  setBranchAccordionCollapsed,
  setEditMode,
  triggerResize,
  updateDepartment,
  removeDepartment,
  updateGroupedDepartments,
  setGroupedDepartments,
} from '../../redux/slices/clientDetailsSlice';
import styles from '../../styles/components/clientdetails/clientDetailsStyle.module.scss';
import {
  isValidDepartmentName,
  nameValidationText,
  descriptionValidationText,
  branchValidationText,
} from '../../utils/clientDetailsUtils';
import useValidation from '../../hooks/client-management/useDepartmentValidation';
import {
  IDepartmentForm,
  IFormField,
  IValidationErrors,
} from '../../types/client-management/departmentTypes';
import AurDepartmentForm from './AurDepartmentForm';
import {useModal} from '../../contexts/modalContext/ModalContext';
import {t} from 'i18next';
import AurAccordion, {
  AurAccordionRef,
} from '../common/aur-accordion/AurAccordion';
import BranchHeader from './BranchHeader';
import AurButton from '../common/aur-button/AurButton';
import AurDrawerHeader from '../common/aur-drawer/AurDrawerHeader';
import {
  deleteClientDepartment,
  updateClientDepartment,
} from '../../api/ClientManagementService';
import {AxiosError} from 'axios';
import {isEmpty} from 'lodash';

interface AurStackedAccordionProps {
  clientId: string;
}

const AurStackedAccordion: React.FC<AurStackedAccordionProps> = ({
  clientId,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [selectedEditDepartment, setSeletedEditDepartment] =
    useState<Department | null>(null);
  const [validationActive, setValidationActive] = useState(false);
  const branches = useSelector(
    (state: RootState) => state.department.branchList
  );
  const [btnDisable, setBtnDisable] = useState<boolean>(true);
  const [userFullName, setUserFullName] = useState<string>('');
  const departmentList = useSelector(
    (state: RootState) => state.department.groupedDepartmentList
  );
  const onCreateMode = useSelector(
    (state: RootState) => state.department.onCreateMode
  );
  const onEditMode = useSelector(
    (state: RootState) => state.department.onEditMode
  );
  const accordionDisabled = useSelector(
    (state: RootState) => state.department.drawerAccordionDisable
  );

  const {showModal} = useModal();

  const formState = useSelector(
    (state: RootState) => state.department.isFormStateChanged
  );

  const [formData, setFormData] = useState<IDepartmentForm>(
    {} as IDepartmentForm
  );
  const {showToast} = useToast();

  const handleValidationErrors = (validationFailures?: IValidationErrors) => {
    if (validationFailures) {
      showModal({
        title: t('clientDetails.modal.departmentDuplicate.title'),
        description: t('clientDetails.modal.departmentDuplicate.description'),
        labelOkButton: t('clientDetails.modal.departmentDuplicate.buttonLabel'),
        buttonStyle: 'single',
      });
    }
  };

  // Handle Edit Department api error response from server
  const handleApiError = (error: AxiosError) => {
    showToast(error.message, 'error', undefined, 'center');
  };

  const DeleteDeparmentApiCall = async (
    clientBranchId: string,
    departmentId: string
  ) => {
    try {
      const response = await deleteClientDepartment({
        data: {
          clientId,
          departmentId: departmentId,
        },
      });

      if (
        !(
          response.data.data?.hasException ||
          response.data.data?.hasValidationError
        )
      ) {
        dispatch(
          removeDepartment({
            clientBranchId: clientBranchId,
            clientDepartmentId: departmentId,
          })
        );
        showToast(
          t('clientDetails.toast.deleteDepartment.deletedSuccessfully'),
          'success'
        );
      } else {
        showToast(
          t('clientDetails.toast.deleteDepartment.deletedFailed'),
          'error'
        );
      }
    } catch (e: any) {
      showToast(e.response.data.data.validationFailures[0].message[0], 'error');
    }
  };

  const user = useSelector(selectUser);

  useEffect(() => {
    if (user) {
      setUserFullName(`${user.name}`);
    }
  }, [user]);

  const makeDepartmentPostRequest = async () => {
    try {
      setBtnDisable(true);
      await updateClientDepartment({
        clientId,
        departmentId: formData.departmentId,
        name: formData.name,
        description: formData.description,
      });
      showToast(
        t('clientDetails.toast.editDepartment.successToastText'),
        'success'
      );
      if (selectedEditDepartment) {
        const updatedDepartment = {
          ...selectedEditDepartment,
          name: formData.name,
          description: formData.description,
          editedOn: new Date().toString(),
          editedBy: userFullName,
        };
        dispatch(
          updateDepartment({
            clientBranchId: selectedEditDepartment?.clientBranchId ?? '',
            clientDepartmentId:
              selectedEditDepartment?.clientDepartmentId ?? '',
            updatedDepartment,
          })
        );
      }
      clearFormState();
    } catch (error: any) {
      setBtnDisable(false);
      if (
        error.response.data.data?.hasException ||
        error.response.data.data?.hasValidationError
      ) {
        handleValidationErrors(error.response.data.data?.validationFailures);
      } else {
        handleApiError(error);
      }
    }
  };

  const {validationState: nameValidation, setValidationError: setNameError} =
    useValidation(
      nameValidationText,
      t('common.validationText.specialCharacterWarning')
    );

  const {
    validationState: descriptionValidation,
    setValidationError: setDescriptionError,
  } = useValidation(
    descriptionValidationText,
    t('common.validationText.maxLengthWarning')
  );
  const {
    validationState: branchValidation,
    setValidationError: setBranchError,
  } = useValidation(branchValidationText, '');

  const validate = (data: IDepartmentForm, key?: keyof IDepartmentForm) => {
    let hasError = false;
    if (!key || key == 'name') {
      if (data.name === '') {
        hasError = true;
        setNameError('required');
      } else if (data.name.length > 50) {
        hasError = true;
        setNameError('maxLength');
      } else if (!isValidDepartmentName(data.name)) {
        hasError = true;
        setNameError('specialChar');
      } else {
        setNameError('');
      }
    }
    if (!key || key == 'description') {
      if (data.description.length > 500) {
        hasError = true;
        setDescriptionError('maxLength');
      } else {
        setDescriptionError('');
      }
    }
    if (!key || key == 'branch') {
      if (!data.branch?.clientBranchId) {
        hasError = true;
        setBranchError('required');
      } else {
        setBranchError('');
      }
    }
    return hasError;
  };

  const setDepartmentFormData = (fieldData: IFormField) => {
    setFormData((previousState) => {
      const newState = {...previousState, [fieldData.name]: fieldData.value};
      validate(newState, fieldData.name);

      if (
        (!isEmpty(newState.name.trim()) &&
          newState.name !== selectedEditDepartment?.name) ||
        newState.description !== selectedEditDepartment?.description ||
        newState.branch.clientBranchId !== selectedEditDepartment.clientBranchId
      ) {
        dispatch(setClientDetailsFormState(true));
        setBtnDisable(false);
      } else {
        setBtnDisable(true);
        dispatch(setClientDetailsFormState(false));
      }

      return {...newState};
    });
  };

  const saveButtonClickHandler = () => {
    setValidationActive(true);
    if (!validate(formData)) {
      makeDepartmentPostRequest();
    }
  };

  const clearFormState = () => {
    setBtnDisable(false);
    setFormData((previousData) => {
      return {...previousData, name: '', description: ''};
    });
    dispatch(setDrawerAccordionDisable(false));
    dispatch(setClientDetailsFormState(false));
    dispatch(setEditMode(false));
    dispatch(triggerResize());
    setValidationActive(false);
    setSeletedEditDepartment(null);
  };

  const noSaveButtonClickHandler = () => {
    if (formState) {
      showModal({
        title: t('common.clientDetailsUnsavedChangePopup.title'),
        description: t('common.clientDetailsUnsavedChangePopup.description'),
        labelCancelButton: t(
          'common.clientDetailsUnsavedChangePopup.cancelButtonLabel'
        ),
        labelOkButton: t(
          'common.clientDetailsUnsavedChangePopup.okButtonLabel'
        ),
        onOk: clearFormState,
      });
      return;
    }

    clearFormState();
  };

  const handleEdit = (department: Department) => {
    setSeletedEditDepartment(department);
    setFormData({
      departmentId: department.clientDepartmentId,
      name: department.name,
      description: department.description,
      branch: branches.filter(
        (_) => _.clientBranchId == department.clientBranchId
      )[0],
    });

    const departments = departmentList.map((item) => {
      if (item.clientBranchId !== department.clientBranchId) {
        return {
          ...item,
          branchExpanded: false,
        };
      }
      return item;
    });
    dispatch(setGroupedDepartments([...departments]));
    collapseAllChildren();
    setNameError('');
    setDescriptionError('');
    setBranchError('');
    dispatch(setEditMode(true));
    dispatch(setDrawerAccordionDisable(true));
    dispatch(setBranchAccordionCollapsed(false));

    setTimeout(() => {
      if (headerRef) {
        headerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, 10);
  };

  const handleDelete = (department: Department) => {
    showModal({
      description: '',
      title: t('common.DepartmentDeleteConfirmPopup.description'),
      labelOkButton: t('common.DepartmentDeleteConfirmPopup.okButtonLabel'),
      labelCancelButton: t(
        'common.DepartmentDeleteConfirmPopup.cancelButtonLabel'
      ),
      onOk: () =>
        DeleteDeparmentApiCall(
          department.clientBranchId,
          department.clientDepartmentId
        ),
    });
  };

  useEffect(() => {
    if (
      descriptionValidation.isError ||
      nameValidation.isError ||
      branchValidation.isError
    ) {
      setBtnDisable(true);
    }
  }, [
    descriptionValidation,
    descriptionValidation,
    branchValidation,
    nameValidation,
  ]);

  const addDeptHeaderButtons = [
    <AurButton
      key={0}
      type="exit"
      title={t('common.unSavedChangesPopup.cancelBtn')}
      label={t('common.unSavedChangesPopup.cancelBtn')}
      size="Medium"
      onClick={noSaveButtonClickHandler}
    />,
    <AurButton
      key={1}
      disabled={btnDisable}
      type="continue"
      label={t('buttons.saveChanges')}
      title={t('buttons.saveChanges')}
      size="Medium"
      onClick={saveButtonClickHandler}
    />,
  ];

  const childAccordionRefs = useRef(
    departmentList.map((parent) =>
      parent.departments.map(() => React.createRef<AurAccordionRef>())
    )
  );

  useEffect(() => {
    if (onCreateMode) {
      const departments = departmentList.map((item) => {
        return {
          ...item,
          branchExpanded: false,
        };
      });
      dispatch(setGroupedDepartments([...departments]));
      collapseAllChildren();
    }
  }, [onCreateMode]);

  const collapseAllChildren = () => {
    childAccordionRefs.current.forEach((childRefs) =>
      childRefs.forEach((childRef) => childRef.current?.collapse())
    );
  };
  return (
    <Box>
      {departmentList.map((item, branchIndex) => (
        <React.Fragment key={branchIndex}>
          <BranchHeader
            branchName={item.clientBranchName.toUpperCase()}
            isExpanded={item.branchExpanded ?? false}
            onClick={() => {
              const updatedItem = {
                ...item,
                branchExpanded: !item.branchExpanded,
              };
              dispatch(updateGroupedDepartments(updatedItem));
            }}
            disabledHeaderIcon={onEditMode || onCreateMode}
          />
          <Collapse
            in={item.branchExpanded}
            timeout="auto"
            unmountOnExit
            data-testid="aur-accordion-test"
          >
            {item.departments.map((department, departmentIndex) => (
              <React.Fragment key={departmentIndex}>
                {(department.clientDepartmentId ==
                  selectedEditDepartment?.clientDepartmentId && (
                  <div className="department-edit-penal form-color">
                    <Typography className="text-wrapper">
                      {t('clientDetails.department.editDepartment')}
                    </Typography>
                    <div ref={headerRef}>
                      <AurDrawerHeader
                        title={selectedEditDepartment.name}
                        titleCssClass={`${styles.addDepartmentHeaderText}`}
                        buttons={addDeptHeaderButtons}
                      />
                      <AurDepartmentForm
                        departmentFormData={formData}
                        branches={branches}
                        isValidationAcive={validationActive}
                        setDepartmentFormData={setDepartmentFormData}
                        SaveButtonClickHandler={saveButtonClickHandler}
                        noSaveButtonClickHandler={noSaveButtonClickHandler}
                        nameValidation={nameValidation}
                        descriptionValidation={descriptionValidation}
                        branchValidation={branchValidation}
                        onEditMode={true}
                        headerLabel={department.name}
                        setBtnDisable={setBtnDisable}
                        btnDisable={btnDisable}
                      />
                    </div>
                  </div>
                )) || (
                  <AurAccordion
                    disabled={accordionDisabled}
                    collapsed={department.expanded ? false : true}
                    title={department.name}
                    className="client-detail-tile-body"
                    alertWarningIcon={false}
                    ref={
                      childAccordionRefs.current[branchIndex][departmentIndex]
                    }
                    titleContent={
                      <TileButtonGroup
                        onDelete={() => handleDelete(department)}
                        onEdit={() => {
                          handleEdit(department);
                        }}
                        disableDelete={onCreateMode || onEditMode}
                        disableEdit={onCreateMode || onEditMode}
                      />
                    }
                  >
                    <AurDetailsContent
                      description={department.description}
                      lastEditedDate={department.editedOn}
                      lastEditedBy={department.editedBy}
                    />
                  </AurAccordion>
                )}
              </React.Fragment>
            ))}
          </Collapse>
        </React.Fragment>
      ))}
    </Box>
  );
};

export default AurStackedAccordion;
